import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PACKAGE_CONFIGS } from "@/config/packages";

// Singleton cache so multiple components mounting at once don't re-fetch.
// Invalidates on page reload (good — admins who tweak prices refresh once, travellers
// on a fresh pageview will see the new prices on every reload).
let _cache = null;
let _loading = null;

// Format an INR integer into Indian-style grouping (e.g. 65000 → "65,000", 185000 → "1,85,000").
function _inr(n) {
  try { return Number(n).toLocaleString("en-IN"); } catch { return String(n); }
}

// Merge an admin override onto the base package config. If the override specifies `price`
// without an explicit `priceLabel`, auto-synthesise the label so a single-field price edit
// keeps the hero headline, price pill and JSON-LD all in sync.
function _mergeOverride(base, patch) {
  if (!patch || Object.keys(patch).length === 0) return base;
  const merged = { ...base, ...patch };
  const priceChanged = patch.price !== undefined && patch.price !== base.price;
  if (priceChanged && patch.priceLabel === undefined) {
    merged.priceLabel = `From \u20b9${_inr(patch.price)} per person.`;
  }
  return merged;
}

// Exposed so the admin CMS can bust the module-level cache right after a save/reset.
// The public-page hooks (usePackageConfig / useAllPackageConfigs) will then re-fetch
// fresh overrides on their next effect run.
export function invalidatePackageConfigCache() {
  _cache = null;
  _loading = null;
}

async function loadOverrides() {
  if (_cache) return _cache;
  if (_loading) return _loading;
  _loading = api.get("/packages/overrides")
    .then((r) => { _cache = r.data || {}; return _cache; })
    .catch(() => { _cache = {}; return _cache; })
    .finally(() => { _loading = null; });
  return _loading;
}

// Returns the package config with admin overrides merged in. Overrides win over any
// key defined in the static config (price, priceLabel, waSave, eyebrow today; extensible).
// While loading, returns the static config so there's never a flash of blank data.
export function usePackageConfig(slug) {
  const base = PACKAGE_CONFIGS[slug] || null;
  const [cfg, setCfg] = useState(base);

  useEffect(() => {
    if (!base) { setCfg(null); return; }
    let alive = true;
    loadOverrides().then((overrides) => {
      if (!alive) return;
      const patch = overrides[slug] || {};
      setCfg(_mergeOverride(base, patch));
    });
    return () => { alive = false; };
  }, [slug, base]);

  return cfg;
}

// For callers that need multiple packages at once (e.g. the /packages grid).
export function useAllPackageConfigs() {
  const [list, setList] = useState(() => Object.values(PACKAGE_CONFIGS));

  useEffect(() => {
    let alive = true;
    loadOverrides().then((overrides) => {
      if (!alive) return;
      setList(
        Object.entries(PACKAGE_CONFIGS).map(([slug, base]) =>
          _mergeOverride(base, overrides[slug] || {})
        )
      );
    });
    return () => { alive = false; };
  }, []);

  return list;
}
